import { useState, useEffect } from "react"
import { PlusCircle, Users, UserCheck, Mail, Loader2, X } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { AdminStats } from "../components/ui/school/admin-management/admin-stats"
import { AdminTable } from "../components/ui/school/admin-management/admin-table"
import { AddAdminModal } from "../components/ui/school/admin-management/add-admin-modal"
import { EditAdminModal } from "../components/ui/school/admin-management/edit-admin-modal"
import { DeleteConfirmModal } from "../components/ui/school/admin-management/delete-confirm-modal"
import { listStaff, inviteStaff, updateStaff, removeStaff, listStudents, inviteParent } from "../hooks/api/schoolApi"

// ── Invite Parent Modal ──────────────────────────────────────────────────────
function InviteParentModal({ student, onClose, onDone }) {
  const [form, setForm] = useState({ email: "", name: "" })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      await inviteParent(student.id, form.email, form.name)
      setSuccess(true)
      setTimeout(() => { onDone(); onClose() }, 1200)
    } catch {
      setError("Failed to invite parent. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-7 w-full max-w-md">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Invite Parent / Guardian</h2>
            <p className="text-sm text-slate-500 mt-0.5">For {student.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        {success ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-100 rounded-xl text-green-700 font-medium">
            <UserCheck className="w-5 h-5 shrink-0" />
            Parent invited successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Parent name</label>
              <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required
                placeholder="e.g. Mrs. Adaeze Okafor"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1 block">Parent email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required
                  placeholder="parent@example.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Inviting…" : "Send Invite"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Guardians Tab ─────────────────────────────────────────────────────────────
function GuardiansTab() {
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviteTarget, setInviteTarget] = useState(null)
  const [search, setSearch] = useState("")

  useEffect(() => {
    listStudents({ limit: 100 })
      .then((res) => {
        const list = (res.students ?? []).map((s) => ({
          id: s._id,
          name: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || s.name || "Student",
          class: s.classLevel ?? s.class ?? "—",
          parentIds: s.parentIds ?? [],
        }))
        setStudents(list)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = students.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()) || s.class.toLowerCase().includes(search.toLowerCase()))

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <input type="text" placeholder="Search students…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-xs px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
        <span className="text-xs text-slate-400">{filtered.length} students</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading students…
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Student</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Class</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase">Parent(s) Linked</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50/50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-xs font-bold text-violet-700">
                        {s.name[0] ?? "?"}
                      </div>
                      <span className="font-semibold text-slate-800">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{s.class}</td>
                  <td className="px-5 py-3">
                    {s.parentIds.length > 0 ? (
                      <span className="flex items-center gap-1.5 text-teal-600 font-semibold text-xs">
                        <UserCheck className="w-4 h-4" /> {s.parentIds.length} linked
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">None</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setInviteTarget(s)}
                      className="text-xs font-semibold text-violet-600 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-3 py-1.5 rounded-lg transition-colors">
                      Invite Parent
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="text-center py-10 text-slate-400 text-sm">No students found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {inviteTarget && (
        <InviteParentModal
          student={inviteTarget}
          onClose={() => setInviteTarget(null)}
          onDone={() => setInviteTarget(null)}
        />
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminManagementPage = () => {
  const [activeTab, setActiveTab] = useState("staff")
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [deleteAdminId, setDeleteAdminId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const load = () => {
    setLoading(true)
    listStaff()
      .then((data) =>
        setAdmins(
          data.map((s) => ({
            id: s._id,
            name: `${s.firstName} ${s.lastName}`,
            email: s.email,
            role: s.role,
            department: s.subjects?.[0] ?? "—",
            permissions: s.permissions ?? {},
            status: s.inviteAccepted ? (s.status === "active" ? "Active" : "Inactive") : "In Review",
          }))
        )
      )
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const totalAdmins = admins.length
  const activeAdmins = admins.filter((a) => a.status === "Active").length
  const totalRoles = new Set(admins.map((a) => a.role)).size

  const adminsPerPage = 10
  const totalPages = Math.ceil(totalAdmins / adminsPerPage)
  const currentAdmins = admins.slice((currentPage - 1) * adminsPerPage, currentPage * adminsPerPage)

  const handleAddAdmin = async (newAdmin) => {
    const [firstName, ...rest] = (newAdmin.name ?? "").split(" ")
    await inviteStaff({
      firstName: firstName ?? newAdmin.name,
      lastName: rest.join(" ") || "-",
      email: newAdmin.email,
      role: newAdmin.role ?? "teacher",
      subjects: newAdmin.department ? [newAdmin.department] : [],
      permissions: newAdmin.permissions ?? {},
    }).catch(() => {})
    load()
  }

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin)
    setIsEditModalOpen(true)
  }

  const handleUpdateAdmin = async (updatedAdmin) => {
    await updateStaff(updatedAdmin.id, {
      role: updatedAdmin.role,
      status: updatedAdmin.status?.toLowerCase() === "active" ? "active" : "inactive",
      permissions: updatedAdmin.permissions ?? {},
    }).catch(() => {})
    setAdmins((prev) => prev.map((a) => (a.id === updatedAdmin.id ? { ...a, ...updatedAdmin } : a)))
  }

  const handleDeleteClick = (id) => {
    setDeleteAdminId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    await removeStaff(deleteAdminId).catch(() => {})
    setAdmins((prev) => prev.filter((a) => a.id !== deleteAdminId))
    setIsDeleteModalOpen(false)
    setDeleteAdminId("")
  }

  const adminToDelete = admins.find((a) => a.id === deleteAdminId)

  const TABS = [
    { id: "staff", label: "Staff & Admins", icon: Users },
    { id: "guardians", label: "Guardians", icon: UserCheck },
  ]

  return (
    <SchoolLayout>
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary mb-2">Staff & Admins</h1>
            <p className="text-muted-foreground">
              Manage staff accounts, roles, permissions, and parent portal access.
            </p>
          </div>
          {activeTab === "staff" && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              Invite Staff
            </Button>
          )}
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 bg-slate-100 rounded-xl p-1 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {activeTab === "staff" && (
          <>
            <AdminStats totalAdmins={totalAdmins} activeAdmins={activeAdmins} totalRoles={totalRoles} />
            {loading ? (
              <div className="flex justify-center py-12 text-gray-400">Loading staff…</div>
            ) : (
              <AdminTable
                admins={currentAdmins}
                onEdit={handleEditAdmin}
                onDelete={handleDeleteClick}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}

        {activeTab === "guardians" && <GuardiansTab />}
      </div>

      <AddAdminModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddAdmin} />

      <EditAdminModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedAdmin(null) }}
        onUpdate={handleUpdateAdmin}
        admin={selectedAdmin}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteAdminId("") }}
        onConfirm={handleConfirmDelete}
        adminName={adminToDelete?.name || ""}
      />
    </SchoolLayout>
  )
}

export default AdminManagementPage
